import { CommonModule } from '@angular/common';
import { Component, Input, input } from '@angular/core';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
    LineChartBaseChart,
    TimeSeriesFacade,
} from '@backoffice-frontend/time-series-identifier/domain';
import type { ClosingValue, SmaGroup, SmaGroupWithIndex, SmaSettings, TimeSeriesInput } from '@clean-code/models';
import { SmaChartData, TimeFrame } from '@clean-code/models';
import { indicate } from '@clean-code/shared/common';
import {
    DevExtremeLineChartComponent,
    LineChartDataModel,
    LineChartModel,
} from '@clean-code/shared/components/ui-devextreme-charts';
import { TranslocoModule } from '@jsverse/transloco';
import { EMPTY } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'fof-ma-chart',
    templateUrl: './ma-chart.component.html',
    imports: [
        CommonModule,
        MatProgressBar,
        TranslocoModule,
        DevExtremeLineChartComponent,
    ]
})
export class MAChartComponent extends LineChartBaseChart<SmaSettings> {
  @Input()
  public set identifier(value: TimeSeriesInput) {
    this.identifier$.next(value);
  }

  @Input()
  public set timeFrame(value: TimeFrame) {
    this.selectedTimeFrame$.next(value);
  }

  @Input()
  public set settings(value: SmaSettings) {
    this.chartSettings$.next(value);
  }

  public legend = input(false);

  public data$ = this.paramChange$.pipe(
    switchMap(
      ([value, settings, timeFrame]: [
        TimeSeriesInput,
        SmaSettings,
        TimeFrame,
      ]) => {
        return this.timeSeriesFacade
          .getMAChartData$(value.identifier, settings, timeFrame)
          .pipe(indicate(this.isLoading$));
      },
    ),
    map((values: SmaGroupWithIndex) => {
      const array = new Array<any>();

      const closedData = {
        label: values.name,
        data: values.closingValues.map(
          (val: ClosingValue) =>
            ({
              date: new Date(val.tradeDate),
              value: val.value,
            }) as LineChartDataModel,
        ),
      };

      array.push(closedData);

      values.groups.forEach((value: SmaGroup) => {
        const data = {
          label: 'MA' + value.name,
          yAxisTitle: values.unit,
          data: value.values.map(
            (val: SmaChartData) =>
              ({
                date: new Date(val.date),
                value: val.sma,
              }) as LineChartDataModel,
          ),
        } as LineChartModel;

        array.push(data);
      });

      return { chart: array, table: values.table };
    }),
  );

  details$ = EMPTY;

  constructor(private timeSeriesFacade: TimeSeriesFacade) {
    super();
  }
}
